import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { FormControl, InputLabel, Select, Switch, FormControlLabel } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import { apiEditBranch, apiFileUpload, apiGeocodeBranchAddress } from '../../api/branch.api';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';

const address_array = ["서울", "경기", "인천", "대전", "대구", "부산", "광주", "울산", "세종", "강원", "충북", "충남", "전북", "전남", "경북", "경남", "제주"];

const BranchEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { branchdata } = location.state;
  const { user, appIsReady } = useData();

  const [formData, setFormData] = useState({
    branch_name: '',
    owner_userid: '',
    owner_name: '',
    owner_phone: '',
    branch_sido: '',
    branch_addr: '',
    use_time: '09:00~22:00',
    setup_date: new Date().toISOString().slice(0, 10),
    information: '1회 30개까지 적립가능',
    branch_image: '',
    latitude: '',
    longitude: '',
    isESG: false, // ESG 기능 활성화 상태 (기본값: false)
    isPET: false, // 지점 기능 활성화 상태 (기본값: false)
  });

  useEffect(() => {
    if (branchdata) {
      setFormData({ ...branchdata, setup_date: formatDate(branchdata.setup_date) });
    }
  }, [branchdata]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateString) => {
    return dateString.slice(0, 10);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Switch 컴포넌트를 위한 핸들러 함수
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  // 좌표 조회는 서버가 대신 한다. 브라우저에서 네이버를 직접 부르면
  // NCP 시크릿을 이 번들에 넣어야 하고, 실제로 그렇게 노출돼 있었다.
  const fetchCoordinates = async (address) => {
    try {
      const { latitude, longitude } = await apiGeocodeBranchAddress(address, user.accessToken);
      setFormData((prevFormData) => ({ ...prevFormData, latitude, longitude }));
    } catch (error) {
      // 조용히 실패하면 관리자는 좌표가 옛 주소인 줄 모른 채 저장한다.
      setSnackbarMessage(
        error?.response?.data?.reason || '주소로 좌표를 찾지 못했습니다. 좌표를 직접 확인해주세요.'
      );
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleAddressBlur = (e) => {
    const address = e.target.value?.trim();
    if (!address) return;
    fetchCoordinates(address);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await apiEditBranch(formData, formData.id, user.accessToken);
      setSnackbarMessage('지점이 정상적으로 수정되었습니다.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setTimeout(() => {
        setIsSubmitting(false);
        navigate(-1); 
      }, 500);
    } catch (error) {
      setSnackbarMessage('Failed to update branch.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await apiFileUpload(uploadFormData, user.accessToken);
      const imageUrl = response;
      setFormData({
        ...formData,
        branch_image: imageUrl
      });
      setFile(null); // Clear the file input
      setPreviewImage(null); // Clear the preview image
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  지점 수정
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="지점 이름"
                        name="branch_name"
                        value={formData.branch_name}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="소유자 아이디"
                        name="owner_userid"
                        value={formData.owner_userid}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="소유자 이름"
                        name="owner_name"
                        value={formData.owner_name}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="소유자 전화번호"
                        name="owner_phone"
                        value={formData.owner_phone}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="branch_sido-label">지점 시/도</InputLabel>
                        <Select
                          labelId="branch_sido-label"
                          name="branch_sido"
                          value={formData.branch_sido}
                          onChange={handleInputChange}
                          sx={{
                            height: 45,
                          }}
                          variants='solid'
                          required
                        >
                          {address_array.map((address, index) => (
                            <MenuItem key={index} value={address}>
                              {address}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="지점 주소"
                        name="branch_addr"
                        value={formData.branch_addr}
                        onChange={handleInputChange}
                        onBlur={handleAddressBlur}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="운영 시간"
                        name="use_time"
                        value={formData.use_time}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="설치 날짜"
                        name="setup_date"
                        type="date"
                        value={formData.setup_date}
                        onChange={handleInputChange}
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox display="flex" alignItems="center" height="56px">
                        <FormControlLabel
                          control={
                            <Switch
                              name="isESG"
                              checked={formData.isESG}
                              onChange={handleSwitchChange}
                              color="primary"
                            />
                          }
                          label={
                            <MDTypography variant="body2" fontWeight="medium">
                              ESG 기능 활성화
                            </MDTypography>
                          }
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <MDBox display="flex" alignItems="center" height="56px">
                        <FormControlLabel
                          control={
                            <Switch
                              name="isPET"
                              checked={formData.isPET}
                              onChange={handleSwitchChange}
                              color="primary"
                            />
                          }
                          label={
                            <MDTypography variant="body2" fontWeight="medium">
                              지점 기능 활성화
                            </MDTypography>
                          }
                        />
                      </MDBox>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="정보"
                        name="information"
                        value={formData.information}
                        onChange={handleInputChange}
                        required
                        multiline
                        rows={4}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="지점 이미지 URL"
                        name="branch_image"
                        value={formData.branch_image}
                        onChange={handleInputChange}
                      />
                      <MDBox mt={2} display="flex" alignItems="center">
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="contained-button-file"
                          type="file"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="contained-button-file">
                          <Button variant="contained" component="span" color="info">
                            파일 선택
                          </Button>
                        </label>
                        <Button variant="contained" color="success" onClick={handleUpload} sx={{ marginLeft: 2 }}>
                          업로드
                        </Button>
                      </MDBox>
                      <MDBox mt={2}>
                        {previewImage && (
                          <img src={previewImage} alt="preview" style={{ width: '20%', marginBottom: 10 }} />
                        )}
                        {!previewImage && formData.branch_image && (
                          <img src={formData.branch_image} alt="branch" style={{ width: '20%', marginBottom: 10 }} />
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                  <Grid container spacing={3} mt={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="위도"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="경도"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        disabled
                      />
                    </Grid>
                  </Grid>
                  <MDBox mt={3} display="flex" justifyContent="flex-end">
                    <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                      {isSubmitting ? '저장 중...' : '저장'}
                    </Button>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BranchEdit;
